// The Download CSV Button takes a list of standard trees with mappings,
// flattens them, and generates a CSV file with the mappings.  When the user
// clicks on the button, the CSV file is "downloaded" for them.  This is used
// for folks who would like to see the content in a spreadsheet instead
// of an in-product offering.
import * as React from "react";

import IconButton from "@khanacademy/wonder-blocks-icon-button";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {useLocation} from "@khan/urls";

import {
    downloadCsvFile,
    toCsvContent,
} from "#download-csv-package/download-csv.ts";

import type {Standard} from "./types.ts";

import csvIcon from "@phosphor-icons/core/regular/file-csv.svg";

type Props = {
    standards: Array<Standard>;
    title: string;
};

export const DownloadCSVButton = (props: Props): React.ReactElement => {
    const {origin} = useLocation();

    const {standards, title} = props;

    const description = `Download Standard Mapping CSV for ${title}`;

    return (
        <Tooltip content={description} placement="right">
            <IconButton
                onClick={() =>
                    downloadStandardMappingCSV(standards, title, origin)
                }
                icon={csvIcon}
                kind="tertiary"
                actionType="neutral"
                aria-label={description}
            />
        </Tooltip>
    );
};

function generateStandardMappingCSV(
    standards: Array<Standard>,
    origin: string,
) {
    // Initialize Headers
    const csvHeaders = [
        "Set ID",
        "Standard ID",
        "Standard Description",
        "Content Kind",
        "Content Title",
        "Content URL",
    ];
    const csvRows: Array<Array<any>> = [];

    const traverse = (standard: Standard) => {
        const commonFields = [
            standard.setId,
            standard.standardId,
            standard.description,
        ];

        for (const contentItem of standard.mappedContent ?? []) {
            csvRows.push([
                ...commonFields,
                contentItem.contentKind,
                contentItem.title,
                `${origin}${contentItem.defaultUrlPath}`,
            ]);
        }

        for (const child of standard.standards ?? []) {
            traverse(child);
        }
    };

    for (const standard of standards) {
        traverse(standard);
    }

    return toCsvContent(csvHeaders, csvRows);
}

function downloadStandardMappingCSV(
    standards: Array<Standard>,
    title: string,
    origin: string,
) {
    const csv = generateStandardMappingCSV(standards, origin);
    downloadCsvFile(`${title} Standard Mappings.csv`, csv);
}

export default DownloadCSVButton;
